/**
 * Auth context. Tracks the Supabase session and whether the signed-in user is
 * staff (email on the company domain). Public agents are simply "not staff".
 */
import type { Session } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { signInWithMicrosoft, signOut, supabase } from '../lib/supabase';

const STAFF_DOMAIN = 'agentsalliance.biz';

interface AuthState {
  session: Session | null;
  loading: boolean;
  email: string | null;
  isStaff: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const value = useMemo<AuthState>(() => {
    const email = session?.user.email ?? null;
    const isStaff = !!email && email.toLowerCase().endsWith('@' + STAFF_DOMAIN);
    return { session, loading, email, isStaff, signIn: signInWithMicrosoft, signOut };
  }, [session, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
